<script setup lang="ts">
import { VirtualList } from '@pkg//ui/components/ui/virtualList';
import EmptyStateIcon from '@pkg/cms-icons/icons/Empty_State_Icon.svg';
import type { Origin, SurpriseBag, SurpriseBagTranslations } from '@pkg/types/surpriseBag';
import { THEME } from '@pkg/config/misc/theme';
import { cn } from '@pkg/ui/lib/utils';
import { capitalize } from '@pkg/utilities/strings';
import { logger } from '@pkg/logger';
import { Loader2 } from 'lucide-vue-next';
import { getBagAvailability } from '@pkg/utilities/surprisebag';
import ChipFilterUi from '@pkg/ui/components/ui/chip/chip-filter/ChipFilterUi.vue';
import { BAG_AVAILABILITY } from '@pkg/types/surpriseBag';
import { ampli } from '@pkg/analytics-helper/src/ampli';
import MapInteractive from './MapInteractive.vue';
import { ResultsView } from '~/app/types/resultsView';

type OriginWithPlaceId = Origin & { place_id?: string };

enum FilterType {
  ALL = 'no_filter',
  SHOW_AVAILABLE_ITEMS = 'show_available_items',
}

const props = defineProps<{
  showList: boolean;
  emptyResults: boolean;
  errorState: boolean;
  locationToSearch: OriginWithPlaceId | null;
  surpriseBags: SurpriseBag[];
  displayBags: SurpriseBag[];
  isFilterLoading: boolean;
  filterBagAvailability: FilterType;
  disabledButton: boolean;
  resultsTitle: string;
  availableBagsFilterLabel?: string;
  emptyStateText?: string;
  errorStateText?: string;
  ctaText: string;
  staticTranslations: SurpriseBagTranslations;
}>();

const emit = defineEmits<{
  'update:filterBagAvailability': [value: FilterType];
  'load-more': [];
  'load-map-data': [];
  'map-changed': [payload: { center: { latitude: number; longitude: number; }; radiusKm: number; zoom?: number }];
  'open-card-modal': [bag: SurpriseBag];
  'item-clicked': [bag: SurpriseBag];
}>();

const { t } = useI18n();

// Use useState to persist user's tab choice across navigation (GDPR compliant)
const activeTabState = useState<ResultsView>('results_active_tab', () => ResultsView.LIST);

// Local ref that syncs with the global state
const activeTab = ref<ResultsView>(activeTabState.value);

// Sync user's tab choice to global state when they change it
watch(activeTab, (newTab) => {
  activeTabState.value = newTab;
});

const hasLoadedMapData = ref(false);

const handleLoadMore = () => {
  emit('load-more');
};

const handleMapTabClick = () => {
  activeTab.value = ResultsView.MAP;
  mapLoading.value = true;
  mapError.value = false;
  ampli.track({
    event_type: 'item_search_results_filter',
    user_properties: {
      filter: ResultsView.MAP,
    },
  });
  if (!hasLoadedMapData.value && props.locationToSearch?.center) {
    emit('load-map-data');
    hasLoadedMapData.value = true;
  }
};

const handleListTabClick = () => {
  activeTab.value = ResultsView.LIST;
  ampli.track({
    event_type: 'item_search_results_filter',
    user_properties: {
      filter: ResultsView.LIST,
    },
  });
};

const toggleAvailabilityFilter = () => {
  const nextFilter = props.filterBagAvailability === FilterType.SHOW_AVAILABLE_ITEMS
    ? FilterType.ALL
    : FilterType.SHOW_AVAILABLE_ITEMS;
  emit('update:filterBagAvailability', nextFilter);
};

const onMapChanged = (payload: { center: { latitude: number; longitude: number; }; radiusKm: number; zoom?: number }) => {
  emit('map-changed', payload);
};

const onMapLoaded = () => {
  mapLoading.value = false;
  mapError.value = false;
};

const onMapError = (error: Error | string) => {
  mapLoading.value = false;
  mapError.value = true;
  logger.error('MapInteractive error:', error);
};

const onOpenCardModal = () => {
  nextTick(() => {
    const element = document.getElementById('search-by-map');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
};

const handleBagCardClick = (bag: SurpriseBag) => {
  ampli.track({
    event_type: 'list_view_item_click',
    user_properties: {
      item_id: bag?.item?.itemId || '',
      store_id: bag?.store?.storeId || '',
      sold_out: getBagAvailability(bag) !== BAG_AVAILABILITY.AVAILABLE,
    },
  });
};

const mapLoading = ref(true);
const mapError = ref(false);

const { public: { flags: { finderMapFlag } } } = useRuntimeConfig();
const mapFlag = computed(() => finderMapFlag === 'true');
const renderMap = computed(() => mapFlag.value && activeTab.value === ResultsView.MAP);

const mapWrapperClasses = computed(() => ({
  's-results__map-wrapper--loading': mapLoading.value,
  's-results__map-wrapper--error': mapError.value,
  's-results__map-wrapper--loaded': !mapLoading.value && !mapError.value,
}));
</script>

<template>
  <PartialSection v-if="showList" :theme="THEME.APP" class="s-results__wrapper">
    <div class="s-results">
      <div class="s-results__menu">
        <div v-if="mapFlag" class="s-results__tabs">
          <button
            :aria-label="capitalize(t('list view'))"
            :class="cn('s-results__tabs__button', { 's-results__tabs__button--active': activeTab === ResultsView.LIST })"
            :is-active="activeTab === ResultsView.LIST"
            @click="handleListTabClick"
          >
            <CmsIcon as="span" name="List" class="text-[1.5rem] s-results__tabs__icon" />
            <p class="s-results__tabs__text">
              {{ capitalize(t('list')) }}
            </p>
          </button>
          <p class="s-results__tabs__text">
            |
          </p>
          <button
            :aria-label="capitalize(t('map view'))"
            :aria-pressed="activeTab === ResultsView.MAP"
            :class="cn('s-results__tabs__button', { 's-results__tabs__button--active': activeTab === ResultsView.MAP })"
            :is-active="activeTab === ResultsView.MAP"
            @click="handleMapTabClick"
          >
            <CmsIcon as="span" name="Map" class="text-[1.5rem] s-results__tabs__icon" />
            <p class="s-results__tabs__text">
              {{ capitalize(t('map')) }}
            </p>
          </button>
        </div>
        <div class="s-results__filter-available-items">
          <ChipFilterUi :filter-name="'Available items'" @click="toggleAvailabilityFilter">
            {{ availableBagsFilterLabel || t('showAvailableItems') }}
          </ChipFilterUi>
        </div>
      </div>
      <p class="s-results__subtitle">
        <span>{{ locationToSearch?.displayName }}</span>
      </p>
      <h3 class="s-results__title">
        {{ resultsTitle }}
      </h3>
      <template v-if="activeTab === ResultsView.LIST">
        <div :class="{ 'filter-loading': isFilterLoading }">
          <VirtualList :items="displayBags" :static-translations="staticTranslations" @item-clicked="handleBagCardClick($event)" />
        </div>
        <div class="w-full flex justify-center mt-[60px] mb-[40px]">
          <button :disabled="disabledButton" class="s-results__cta" @click="handleLoadMore">
            {{ ctaText }}
          </button>
        </div>
      </template>
      <template v-if="renderMap">
        <div id="search-by-map" :class="cn('s-results__map-wrapper', mapWrapperClasses)">
          <div v-if="mapLoading" class="loading-wrapper">
            <Loader2 class="w-64 h-64 mr-8 animate-spin loader-graphic" />
          </div>
          <div v-if="mapError" class="error-label">
            <p>
              {{ t('zodI18n.errors.browser_settings') }}
            </p>
          </div>
          <MapInteractive
            :address="locationToSearch?.displayName || ''"
            :coordinates="locationToSearch?.center"
            :surprise-bags="surpriseBags"
            :static-translations="staticTranslations"
            :full-page="true"
            @map-changed="onMapChanged"
            @map-loaded="onMapLoaded"
            @map-error="onMapError"
            @open-card-modal="onOpenCardModal"
          />
        </div>
      </template>
    </div>
  </PartialSection>
  <PartialSection v-if="emptyResults && !showList" :theme="THEME.APP" class="s-empty">
    <img class="s-empty__img" :src="EmptyStateIcon" alt="No">
    <p class="s-empty__text">
      {{ emptyStateText }}
    </p>
  </PartialSection>
  <PartialSection v-if="errorState && !showList" :theme="THEME.APP" class="s-empty">
    <img class="s-empty__img" :src="EmptyStateIcon" alt="Empty state icon">
    <p class="s-empty__text">
      {{ errorStateText }}
    </p>
  </PartialSection>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-results {
  display: flex;
  flex-direction: column;
  margin: theme('spacing.0') theme('spacing.4');

  @screen tablet {
    margin: theme('spacing.0') auto;
  }

  &__cta {
    @mixin cta-app;
    width: fit-content;
    padding: theme('spacing.8') theme('spacing.20');

    &:hover {
        background-color: theme('colors.primary.20');
      }

    &:disabled {
      background-color: theme('colors.neutral.30');
      color: theme('colors.neutral.60');
      cursor: not-allowed;

      &:hover {
        background-color: theme('colors.neutral.30');
        color: theme('colors.neutral.60');
      }
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;

    @screen tablet-wide {
      flex-direction: row;
      margin: theme('spacing.0') theme('spacing.96');
    }

    @screen desk {
      flex-direction: row;
      margin: theme('spacing.0') theme('spacing.120');
    }
  }

  &__title {
    @mixin h5-tenon;
    padding-bottom: theme('spacing.20');
    font-weight: bold;
    color: var(--theme-text-color-app);
  }

  &__map-wrapper {
    margin-bottom: theme('spacing.32');

    &--loading,
    &--error {
      border: 1px solid var(--theme-heading-color-app);
      position: relative;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;

      .loading-wrapper {
        position: absolute;
        text-align: center;
        top: calc(50% - 32px);
        left: calc(50% - 32px);
      }

      .loader-graphic {
        width: 64px;
        height: 64px;
        color: var(--theme-heading-color-app);
      }

      .error-label {
        position: absolute;
        top: 50%;
        width: 100%;
        text-align: center;
        color: var(--input-active-border);
        font-weight: bold;
        font-size: var(--font-size-tabletwide);
      }
    }

    &--error {
      border-color: var(--input-active-border);
    }
  }

  &__menu {
    display: flex;
    justify-content: space-between;
    padding-bottom: theme('spacing.24');
    flex-direction: column;
    gap: theme('spacing.16');

    @screen tablet-wide {
      flex-direction: row;
      justify-content: space-between;
      padding-bottom: theme('spacing.16');
      gap: theme('spacing.0');
    }
  }

  &__subtitle {
    display: flex;
    align-items: flex-end;
    gap: theme('spacing.4');
    color: var(--theme-heading-color-app);
    padding-bottom: theme('spacing.16');
    background-repeat: no-repeat;

    &::before {
      content: '';
      width: 25px;
      height: 20px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='17' height='16' viewBox='0 0 17 16'%3E%3Cpath fill='%235F6D6D' d='M7.32706 9.65384L1.56556 7.32502C1.36812 7.2545 1.22133 7.14308 1.12518 6.99077C1.02903 6.83847 0.980957 6.66379 0.980957 6.46674C0.980957 6.26969 1.03256 6.09732 1.13576 5.94964C1.23896 5.80196 1.38543 5.69119 1.57518 5.61734L15.3982 0.453915C15.5779 0.383398 15.7487 0.372498 15.9108 0.421214C16.0729 0.469931 16.211 0.55134 16.3251 0.66544C16.4392 0.77954 16.5206 0.917623 16.5693 1.07969C16.618 1.24176 16.6071 1.41265 16.5366 1.59236L11.3347 15.4211C11.2475 15.6224 11.1264 15.7676 10.9712 15.8567C10.8161 15.9458 10.6532 15.9903 10.4828 15.9903C10.3123 15.9903 10.153 15.9437 10.0049 15.8504C9.85684 15.7571 9.74242 15.614 9.66166 15.4211L7.32706 9.65384ZM10.4674 13.1635L14.4828 2.49809L3.79818 6.49424L8.55511 8.40654L10.4674 13.1635Z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position-x: left;
      display: inline-block;

      @screen tablet {
        margin-left: -30px;
      }
    }

    @screen tablet {
      padding-bottom: theme('spacing.24');
      margin: 0 -30px !important;
      margin: 0 calc(-1 * var(--arrow-offset)) !important;
    }

    span {
      display: block;
      padding: 0 var(--arrow-offset);
    }
  }

  &__title,
  &__subtitle {

    @screen tablet {
      margin: 0 auto;
      width: 340px;
    }

    @screen tablet-wide {
      width: 100%;
    }

    span {
      display: block;
    }
  }

  &__filter {
    display: flex;
    padding-bottom: theme('spacing.16');

    @screen tablet-wide {
      justify-content: flex-end;
    }

    &-available-items {
      display: flex;
      gap: theme('spacing.8');
    }
  }

  .filter-loading {
    opacity: 0.7;
    transition: opacity 0.2s ease-in-out;
  }

  &__tabs {
    display: flex;
    gap: theme('spacing.8');

    &__button {
      display: flex;
      gap: theme('spacing.8');

      &--active {
        padding-bottom: theme('spacing.8');
        border-bottom: 2px solid var(--theme-heading-color-app);
      }
    }

    &__text {
      color: var(--theme-heading-color-app);
      font-weight: bold;
    }

    &__icon {
      color: var(--theme-heading-color-app);
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    padding: theme('spacing.40') theme('spacing.16') 0 theme('spacing.16');

    @screen tablet {
      padding-left: 0;
      padding-right: 0;
    }

  }
}

.list-view,
.map-view {
  width: theme('spacing.40');
  background-color: var(--theme-heading-color-app);

  &.active {
    background-color: black;
  }

  &:hover {
    background-color: var(--theme-heading-color-app);
  }
}

.s-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: theme('spacing.96') theme('spacing.32') theme('spacing.120') theme('spacing.32');

  &__text {
    padding-top: theme('spacing.20');
    color: theme('colors.neutral.70');
    width: 70%;
    text-align: center;
  }

  &__img {
    width: 170px;
    height: auto;
  }
}
</style>
